import Phaser from 'phaser';
import { SceneKeys } from './SceneKeys';
import { loadMetaState, saveMetaState } from '../core/save';
import { Rng } from '../core/rng';
import { TEX } from '../core/TextureFactory';
import { audio } from '../core/AudioManager';
import { chapters } from '../data';
import {
  applyRunRewards,
  computeRunRewards,
  type RunOutcome,
  type RunResultOutcome,
  type RunRewardResult,
} from '../meta';
import { HEX } from '../ui/lobby/palette';
import { ResultsRewardRow } from './ResultsRewardRow';

/** Colores de los trocitos de confeti (paleta alegre, coherente con el resto de la UI). */
const CONFETTI_COLORS = [0xffd166, 0x6fcf6f, 0x6fb7ff, 0xb98aff, 0xff8a3d, 0xffffff];

/**
 * Datos que la escena Game (u otro punto de entrada) debe pasar al navegar
 * aquí vía `this.scene.start(SceneKeys.Results, data)`. Es el contrato que
 * consume el integrador de F2 al conectar el final de una run.
 */
export interface ResultsSceneData {
  /** Índice (0-indexado) del capítulo jugado, dentro de `data/chapters.json`. */
  chapterIndex: number;
  outcome: RunResultOutcome;
  /** Oleadas completamente superadas en esta run (incluye el boss si se ganó). */
  wavesCleared: number;
}

/**
 * Pantalla de resultados: ¡GANASTE! / DERROTA + fila de recompensas
 * animada. Calcula y aplica la recompensa de fin de run (monedas, XP, gear,
 * progreso de capítulo) sobre el MetaState guardado, y navega al Lobby al
 * tocar la pantalla.
 */
export class ResultsScene extends Phaser.Scene {
  private resultsData!: ResultsSceneData;

  constructor() {
    super(SceneKeys.Results);
  }

  init(data: Partial<ResultsSceneData>): void {
    // Defaults defensivos: permite abrir esta escena directamente (sin venir
    // de Game, p. ej. en pruebas manuales) sin que explote.
    this.resultsData = {
      chapterIndex: data.chapterIndex ?? 0,
      outcome: data.outcome ?? 'won',
      wavesCleared: data.wavesCleared ?? 0,
    };
  }

  create(): void {
    const { width, height } = this.scale;
    const { outcome, chapterIndex, wavesCleared } = this.resultsData;
    const won = outcome === 'won';

    this.cameras.main.setBackgroundColor(HEX.bg);

    const chapter = chapters[chapterIndex] ?? chapters[0];
    let meta = loadMetaState();
    let reward: RunRewardResult = { coins: 0, xp: 0, loot: [] };

    if (chapter) {
      const rng = new Rng((Date.now() ^ 0x2545f491) >>> 0);
      reward = computeRunRewards(chapter, outcome, wavesCleared, rng);
      const runOutcome: RunOutcome = { chapterIndex, outcome, wavesCleared };
      meta = applyRunRewards(meta, runOutcome, reward);
      saveMetaState(meta);
    }

    this.drawBackground(width, height, won);

    if (won) {
      audio.playVictory();
      this.spawnConfetti(width, height);
    } else {
      audio.playDefeat();
    }

    this.add
      .text(width / 2, height * 0.16, won ? '¡GANASTE!' : 'DERROTA', {
        fontFamily: 'monospace',
        fontSize: '42px',
        color: won ? '#ffd166' : '#e0524d',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setShadow(0, 3, '#00000066', 4);

    this.add
      .text(
        width / 2,
        height * 0.16 + 42,
        won ? `${(chapter?.name ?? 'CAPÍTULO').toUpperCase()} COMPLETADO` : 'inténtalo de nuevo',
        {
          fontFamily: 'monospace',
          fontSize: '14px',
          color: '#a99fd1',
        }
      )
      .setOrigin(0.5);

    this.add
      .text(width / 2, height * 0.3, 'RECOMPENSAS', {
        fontFamily: 'monospace',
        fontSize: '13px',
        color: '#6f6394',
      })
      .setOrigin(0.5);

    new ResultsRewardRow(this, width / 2, height * 0.48, width - 48, reward);

    const continueHint = this.add
      .text(width / 2, height * 0.86, 'toca para continuar', {
        fontFamily: 'monospace',
        fontSize: '14px',
        color: '#ffd166',
      })
      .setOrigin(0.5)
      .setAlpha(0);

    this.tweens.add({
      targets: continueHint,
      alpha: { from: 0.3, to: 1 },
      duration: 700,
      delay: 500,
      yoyo: true,
      repeat: -1,
    });

    this.add
      .zone(0, 0, width, height)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.scene.start(SceneKeys.Lobby));
  }

  private drawBackground(width: number, height: number, won: boolean): void {
    const g = this.add.graphics();
    const top = won ? 0x2c1f4d : 0x2c1414;
    const bottom = won ? 0x1b1030 : 0x1b0d0d;
    g.fillGradientStyle(top, top, bottom, bottom, 1);
    g.fillRect(0, 0, width, height);
  }

  /** Confeti de victoria: trocitos de color cayendo desde arriba, sin física — solo tweens. */
  private spawnConfetti(width: number, height: number): void {
    const count = 46;
    for (let i = 0; i < count; i++) {
      const x = Phaser.Math.Between(0, width);
      const startY = Phaser.Math.Between(-60, -10);
      const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length]!;
      const piece = this.add
        .image(x, startY, TEX.pixel)
        .setDisplaySize(Phaser.Math.Between(5, 9), Phaser.Math.Between(9, 14))
        .setTint(color)
        .setDepth(60)
        .setRotation(Phaser.Math.FloatBetween(0, Math.PI));

      this.tweens.add({
        targets: piece,
        y: height + 40,
        x: x + Phaser.Math.Between(-60, 60),
        rotation: piece.rotation + Phaser.Math.FloatBetween(3, 8),
        duration: Phaser.Math.Between(1400, 2400),
        delay: Phaser.Math.Between(0, 500),
        ease: 'Sine.easeIn',
        onComplete: () => piece.destroy(),
      });
    }
  }
}
